/**
 * The Results screen's instant-replay theater (VISION §8): a broadcast TV over the results that plays the match's
 * top plays as VHS tapes (resimulated from the recorded inputs by the app's `ReplayService`; this component only
 * provides the screen element and the controls). ◀ ▶ switch tapes, confirm rewinds the current one, back closes.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { useServices, type ReplayTheaterHandle, type ReplayTheaterState } from '../actions';
import { Hints } from '../components';
import { playTitle } from '@hf/client-core/ui-model/moments';
import { LAYER, useMenuLayer } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import type { ReplayClipInfo } from '@hf/client-core/ui-model/types';
import { fmtTime } from '@hf/client-core/ui-model/screens';

export function ReplayTheaterOverlay({
  clips,
  start,
  onClose,
}: {
  clips: readonly ReplayClipInfo[];
  start: number;
  onClose: () => void;
}) {
  const { replays, audio } = useServices();
  const screen = useRef<HTMLDivElement | null>(null);
  const handle = useRef<ReplayTheaterHandle | null>(null);
  const [index, setIndex] = useState(start);
  const [state, setState] = useState<ReplayTheaterState>('loading');

  useEffect(() => {
    const el = screen.current;
    if (!el || !replays) {
      setState('error');
      return;
    }
    const h = replays.open(
      el,
      clips.map((c) => c.id),
      start,
      { onClip: (i) => setIndex(i), onState: (s) => setState(s) },
    );
    handle.current = h;
    return () => {
      h.close();
      handle.current = null;
    };
  }, []);

  const go = (i: number): void => {
    const n = clips.length;
    if (n === 0) return;
    playSfx(audio, UI_SFX.move);
    handle.current?.play(((i % n) + n) % n);
  };
  const close = (): void => {
    playSfx(audio, UI_SFX.back);
    onClose();
  };
  useMenuLayer(
    (a) => {
      if (a === 'left' || a === 'up') go(index - 1);
      else if (a === 'right' || a === 'down') go(index + 1);
      else if (a === 'confirm') go(index);
      else if (a === 'back' || a === 'pause') close();
    },
    true,
    LAYER.MODAL,
  );

  const clip = clips[index] ?? clips[0];
  return (
    <div class="hf-theater interactive" role="dialog" aria-modal="true" aria-label="Instant replay">
      <div class="hf-theater__backdrop" onClick={close} />
      <div class="hf-theater__tv">
        <div class="hf-theater__bar t-broadcast">
          <span class="hf-theater__rec">
            <i aria-hidden="true" /> INSTANT REPLAY
          </span>
          <span class="hf-theater__count">
            TAPE {index + 1} / {clips.length}
          </span>
          <button class="hf-theater__x interactive" aria-label="Close replay" onClick={close}>
            ✕
          </button>
        </div>
        <div class="hf-theater__screen" ref={screen} data-state={state}>
          {state === 'loading' ? (
            <div class="hf-theater__static t-broadcast" aria-live="polite">
              LOADING TAPE…
            </div>
          ) : null}
          {state === 'error' ? <div class="hf-theater__static t-broadcast">TAPE UNAVAILABLE</div> : null}
        </div>
        {clip ? (
          <div class={`hf-theater__caption tone-${clip.tone}`}>
            <b class="t-display">{playTitle(clip.title)}</b>
            <span class="t-broadcast">
              {clip.sub ? `${clip.sub} · ` : ''}
              {fmtTime(clip.matchSec)}
            </span>
          </div>
        ) : null}
        <div class="hf-theater__tapes">
          {clips.map((c, i) => (
            <button
              key={c.id}
              class={`hf-theater__tape interactive tone-${c.tone} ${i === index ? 'on' : ''}`}
              onClick={() => go(i)}
              aria-label={`Play ${playTitle(c.title)}`}
              aria-pressed={i === index}
            >
              <span class="t-broadcast">{i + 1}</span>
              <b class="t-display">{playTitle(c.title)}</b>
            </button>
          ))}
        </div>
      </div>
      <Hints
        class="hf-theater__hints"
        items={[
          ['left|right', 'TAPES'],
          ['confirm', state === 'ended' ? 'WATCH AGAIN' : 'REWIND'],
          ['back', 'CLOSE'],
        ]}
      />
    </div>
  );
}
